import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NotificationData } from '../../../services/notification.service';
import {
  ModalStateService,
  NotificationModalOpenBehavior,
} from '../../../services/modal-state.service';
import { DialogMutexService } from '../../../services/dialog-mutex.service';
import { LoggerService } from '../../../services/logger.service';
import { VehiclesWithMembershipsResponse } from '../../../services/vehicle.service';
import type { WayNormalizedPurchase } from '../../way-purchase-modal/models/way-purchase.models';
import { MultiEligibleModalMode } from '../models/multi-eligible.models';

export interface MultiEligibleSelectionModalState {
  isOpen: boolean;
  mode: MultiEligibleModalMode;
  notification: NotificationData | null;
  phoneSeed?: string | null;
  /** Pre-fetched phone memberships — skip "Find customer by phone" and open Choose Vehicle. */
  phoneLookupPayload?: VehiclesWithMembershipsResponse | null;
  /** Pre-fetched live Way singles to merge into the phone chooser. */
  phoneWayPurchases?: WayNormalizedPurchase[] | null;
  shouldStore?: boolean;
}

export interface OpenMultiEligibleSelectionModalOptions {
  notification: NotificationData;
  behavior?: NotificationModalOpenBehavior;
  shouldStore?: boolean;
}

export interface OpenMultiEligiblePhoneLookupOptions {
  phone?: string;
  behavior?: NotificationModalOpenBehavior;
  /** When set (e.g. Create order already called the API), jump straight to Choose Vehicle. */
  lookupPayload?: VehiclesWithMembershipsResponse | null;
  /** Live Way singles already fetched with the memberships lookup (optional). */
  wayPurchases?: WayNormalizedPurchase[] | null;
}

@Injectable({ providedIn: 'root' })
export class MultiEligibleSelectionModalStateService {
  private readonly modalStateSubject = new BehaviorSubject<MultiEligibleSelectionModalState>({
    isOpen: false,
    mode: 'lpr',
    notification: null,
    phoneSeed: null,
    phoneLookupPayload: null,
    phoneWayPurchases: null,
    shouldStore: true,
  });

  readonly modalState$ = this.modalStateSubject.asObservable();

  constructor(
    private readonly logger: LoggerService,
    private readonly modalStateService: ModalStateService,
    private readonly dialogMutex: DialogMutexService
  ) {
    this.dialogMutex.releases$.subscribe(owner => {
      if (owner !== 'feature-modal' || !this.modalStateSubject.value.isOpen) {
        return;
      }
      // Another dialog stole the mutex — sync closed. If we still own feature-modal
      // (re-acquire during replace open), keep the modal open.
      if (this.dialogMutex.hasOwner('feature-modal')) {
        return;
      }
      this.modalStateSubject.next({
        isOpen: false,
        mode: 'lpr',
        notification: null,
        phoneSeed: null,
        phoneLookupPayload: null,
        phoneWayPurchases: null,
        shouldStore: true,
      });
    });
  }

  getCurrentState(): MultiEligibleSelectionModalState {
    return this.modalStateSubject.value;
  }

  openModal(options: OpenMultiEligibleSelectionModalOptions): void {
    this.openInternal({
      mode: 'lpr',
      notification: options.notification,
      phoneSeed: null,
      phoneLookupPayload: null,
      phoneWayPurchases: null,
      behavior: options.behavior,
      shouldStore: options.shouldStore,
    });
  }

  /** Attendant phone → Choose Vehicle → Choose Membership/Package flow (CWS-889). */
  openPhoneLookup(options: OpenMultiEligiblePhoneLookupOptions = {}): void {
    this.openInternal({
      mode: 'phone',
      notification: null,
      phoneSeed: options.phone?.trim() || null,
      phoneLookupPayload: options.lookupPayload ?? null,
      phoneWayPurchases: options.wayPurchases ?? null,
      behavior: options.behavior ?? 'replace',
      shouldStore: false,
    });
  }

  hideModal(): void {
    this.modalStateSubject.next({
      isOpen: false,
      mode: 'lpr',
      notification: null,
      phoneSeed: null,
      phoneLookupPayload: null,
      phoneWayPurchases: null,
      shouldStore: true,
    });
    this.dialogMutex.release('feature-modal');
  }

  private openInternal(params: {
    mode: MultiEligibleModalMode;
    notification: NotificationData | null;
    phoneSeed: string | null;
    phoneLookupPayload: VehiclesWithMembershipsResponse | null;
    phoneWayPurchases?: WayNormalizedPurchase[] | null;
    behavior?: NotificationModalOpenBehavior;
    shouldStore?: boolean;
  }): void {
    const {
      mode,
      notification,
      phoneSeed,
      phoneLookupPayload,
      phoneWayPurchases = null,
      behavior = 'respect_state',
      shouldStore = true,
    } = params;

    if (behavior === 'replace') {
      // Cancel any NotificationModal close-animation drain / queued popups that would
      // forceAcquire('notification-modal') ~350ms later and preempt this feature modal.
      this.modalStateService.clearNotificationQueue();
      if (this.modalStateService.getCurrentState().isOpen) {
        this.modalStateService.hideModal();
      }
      this.dialogMutex.cancelDeferred('notification-modal');
      this.dialogMutex.cancelDeferred('way-purchase-modal');
      this.dialogMutex.forceAcquire('feature-modal');
      this.modalStateSubject.next({
        isOpen: true,
        mode,
        notification,
        phoneSeed,
        phoneLookupPayload,
        phoneWayPurchases,
        shouldStore,
      });
      if (shouldStore !== false && notification) {
        this.modalStateService.storeNotificationInLocalStorage(notification);
      }
      return;
    }

    if (
      this.modalStateService.getCurrentState().isOpen ||
      this.dialogMutex.hasOwner('notification-modal') ||
      this.dialogMutex.hasOwner('way-purchase-modal')
    ) {
      this.logger.debug(
        'Deferring multi-eligible selection modal until other dialog releases the mutex.'
      );
      if (shouldStore !== false && notification) {
        this.modalStateService.storeNotificationInLocalStorage(notification);
      }
      this.dialogMutex.defer('feature-modal', () => {
        this.openInternal({
          mode,
          notification,
          phoneSeed,
          phoneLookupPayload,
          phoneWayPurchases,
          behavior: 'respect_state',
          shouldStore: false,
        });
      });
      return;
    }

    if (!this.dialogMutex.tryAcquire('feature-modal')) {
      this.logger.debug('Multi-eligible modal blocked by dialog mutex; storing notification.');
      if (shouldStore !== false && notification) {
        this.modalStateService.storeNotificationInLocalStorage(notification);
      }
      return;
    }

    this.modalStateSubject.next({
      isOpen: true,
      mode,
      notification,
      phoneSeed,
      phoneLookupPayload,
      phoneWayPurchases,
      shouldStore,
    });
    if (shouldStore !== false && notification) {
      this.modalStateService.storeNotificationInLocalStorage(notification);
    }
  }
}
